import {
  Monitor,
  Users,
  Blend,
  MapPin,
  Lightbulb,
  Brain,
  Gamepad2,
  Smartphone,
  Zap,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";

const Methodology = () => {
  const deliveryMethods = [
    {
      title: "Face-to-Face Training",
      icon: Users,
      description:
        "Interactive workshops and seminars with hands-on practical exercises",
      features: [
        "Interactive workshops and seminars",
        "Hands-on practical exercises",
        "Group discussions and case studies",
        "Role-playing and simulations",
        "Peer learning and collaboration",
      ],
    },
    {
      title: "Online Training",
      icon: Monitor,
      description:
        "Virtual learning platforms with self-paced modules and interactive content",
      features: [
        "Virtual learning platforms",
        "Self-paced e-learning modules",
        "Interactive webinars",
        "Online assessments and certifications",
        "Digital resource libraries",
      ],
    },
    {
      title: "Blended Learning",
      icon: Blend,
      description:
        "Combination of online and offline methods for flexible learning experiences",
      features: [
        "Flexible scheduling and pacing",
        "Personalized learning paths",
        "Continuous support and mentoring",
        "Integrated assessment systems",
      ],
    },
    {
      title: "On-Site Training",
      icon: MapPin,
      description:
        "Customized client-premises programs with tailored content and case studies",
      features: [
        "Customized client-premises programs",
        "Tailored content and case studies",
        "Immediate application opportunities",
        "Team building integration",
      ],
    },
  ];

  const learningPrinciples = [
    {
      title: "Experiential and Practical Learning",
      icon: Lightbulb,
      description:
        "Learn by doing with real-world applications and hands-on practice",
    },
    {
      title: "Problem-based Learning Approaches",
      icon: Brain,
      description:
        "Solve real business challenges while developing new skills and knowledge",
    },
    {
      title: "Self-directed Learning Opportunities",
      icon: Users,
      description:
        "Take control of your learning journey with personalized development paths",
    },
    {
      title: "Collaborative and Social Learning",
      icon: Users,
      description:
        "Learn from peers and experts through group activities and discussions",
    },
    {
      title: "Reflective Practice and Application",
      icon: Monitor,
      description:
        "Reflect on learning experiences and apply insights to workplace situations",
    },
  ];

  const technologyEnhanced = [
    {
      title: "Interactive Multimedia Content",
      icon: Monitor,
      description:
        "Engaging videos, presentations, and interactive learning materials",
    },
    {
      title: "Virtual Reality and Simulations",
      icon: Gamepad2,
      description:
        "Immersive learning experiences for complex skill development",
    },
    {
      title: "Gamification and Engagement Tools",
      icon: Gamepad2,
      description:
        "Game-based learning elements to increase motivation and retention",
    },
    {
      title: "Mobile Learning Applications",
      icon: Smartphone,
      description:
        "Learn on-the-go with mobile-optimized content and microlearning",
    },
    {
      title: "AI-powered Personalization",
      icon: Zap,
      description:
        "Adaptive learning systems that customize content to individual needs",
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-blue-50 via-white to-blue-100 text-center" id="methodology">
        <div className="container mx-auto px-4 max-w-5xl space-y-8 animate-fade-in-up">
          <h1 className="text-4xl lg:text-6xl font-extrabold text-blue-900">
            Our <span className="text-primary">Methodology</span>
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
            Discover our innovative training methodologies that combine proven
            adult learning principles with cutting-edge technology for maximum
            impact.
          </p>
        </div>
      </section>

      {/* Training Delivery Methods */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 space-y-1">
            <h2 className="text-3xl lg:text-4xl font-bold text-blue-800">
              Training Delivery Methods
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer flexible delivery formats to meet your organization's unique needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {deliveryMethods.map((method, index) => (
              <Card key={index} className="hover:scale-[1.02] transition-transform duration-300 shadow-md hover:shadow-xl bg-gradient-to-r from-white to-blue-50">
                <CardContent className="p-8 space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <method.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-900">
                      {method.title}
                    </h3>
                  </div>
                  <p className="text-gray-700">{method.description}</p>
                  <ul className="space-y-2">
                    {method.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Philosophy */}
      <section className="py-20 bg-gradient-to-r from-blue-100 via-white to-blue-200">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-blue-800">
              Our Training Philosophy
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Built on proven adult learning principles that ensure maximum engagement and retention.
            </p>
          </div>

          <div className="space-y-12">
            <div>
              <h3 className="text-2xl font-semibold text-center text-blue-700 mb-8">
                Adult Learning Principles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {learningPrinciples.map((principle, index) => (
                  <Card key={index} className="text-center shadow-md hover:shadow-lg bg-white hover:bg-blue-50 transition duration-300">
                    <CardContent className="p-6 space-y-4">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                        <principle.icon className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-semibold text-blue-900 text-sm">
                        {principle.title}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {principle.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-center text-blue-700 mb-8">
                Technology-Enhanced Learning
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {technologyEnhanced.map((tech, index) => (
                  <Card key={index} className="text-center shadow-md hover:shadow-lg bg-white hover:bg-blue-50 transition duration-300">
                    <CardContent className="p-6 space-y-4">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                        <tech.icon className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-semibold text-blue-900 text-sm">
                        {tech.title}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {tech.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-blue-900">
              Quality Assurance & Continuous Improvement
            </h2>
            <p className="text-lg text-gray-600">
              Our commitment to excellence ensures every training program meets the highest standards.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[Users, Monitor, Lightbulb].map((Icon, i) => (
              <Card key={i} className="text-center shadow-md hover:shadow-lg bg-blue-50">
                <CardContent className="p-8 space-y-4">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-blue-900">
                    {[
                      "Pre-Training Assessment",
                      "Real-time Monitoring",
                      "Post-Training Evaluation",
                    ][i]}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {[
                      "Comprehensive needs analysis and learner profiling to customize content",
                      "Continuous tracking of learning progress and engagement levels",
                      "Comprehensive impact assessment and follow-up support",
                    ][i]}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center space-y-8">
          <h2 className="text-3xl lg:text-4xl font-bold">
            Experience Our Methodology
          </h2>
          <p className="text-lg max-w-2xl mx-auto">
            See how our proven methodologies can transform your organization's learning and development outcomes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button variant="secondary" size="lg" asChild>
              <Link to="/contact">Schedule a Demo</Link>
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary"
              asChild
            >
              <Link to="/programs">View Programs</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Methodology;
